import { StandardEvents } from '@shopify/events';

/**
 * Precio Mates Ramos: total + desglose del grabado.
 *
 * No calcula precios. Escucha el mismo evento de cambio de variante que usa
 * Horizon (`productSelect`), espera al HTML que la sección ya ha vuelto a
 * renderizar y sustituye su propio cuerpo por el nuevo. Así el desglose, la
 * oferta y el formato de moneda los sigue resolviendo Liquid.
 *
 * Única excepción: el grabado como servicio aparte (mr-engraving en modo
 * servicio). La variante no lo incluye, así que aquí se suma su precio al de
 * la variante (y al tachado, si lo hay) y se añade el desglose.
 */
class MrProductPrice extends HTMLElement {
  #section = null;

  connectedCallback() {
    this.#section = this.closest('.shopify-section, dialog');
    this.#section?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.#section?.addEventListener('mr:extras-changed', this.#applyService);
    this.#applyService();
  }

  disconnectedCallback() {
    this.#section?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.#section?.removeEventListener('mr:extras-changed', this.#applyService);
    this.#section = null;
  }

  #handleProductSelect = (event) => {
    // Las tarjetas de producto disparan el mismo evento; no nos afectan.
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, newProduct } = detail;

        if (newProduct) {
          this.dataset.productId = newProduct.id;
        } else if (detail.productId && detail.productId !== this.dataset.productId) {
          return;
        }

        const updated = html.querySelector(`mr-product-price[data-block-id="${this.dataset.blockId}"]`);
        const newBody = updated?.querySelector('[data-mr-price-body]');
        const currentBody = this.querySelector('[data-mr-price-body]');

        if (!newBody || !currentBody) return;

        currentBody.replaceWith(newBody);
        this.#applyService();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[mr-product-price] Event promise rejected:', error);
      });
  };

  /** Precio del grabado elegido en el bloque de grabado en modo servicio, en céntimos. */
  get #servicePrice() {
    let total = 0;
    for (const engraving of this.#section?.querySelectorAll('mr-engraving[data-mr-service="true"]') ?? []) {
      if (engraving instanceof HTMLElement) total += Number(engraving.dataset.mrServicePrice) || 0;
    }
    return total;
  }

  #applyService = () => {
    const body = this.querySelector('[data-mr-price-body]');
    if (!(body instanceof HTMLElement) || !body.dataset.basePrice) return;
    if (!this.#section?.querySelector('mr-engraving[data-mr-service="true"]')) return;

    const base = Number(body.dataset.basePrice) || 0;
    const compare = Number(body.dataset.baseCompare) || 0;
    const service = this.#servicePrice;

    setAmount(body.querySelector('.price'), this.#money(base + service));
    if (compare > 0) setAmount(body.querySelector('.compare-at-price'), this.#money(compare + service));

    const row = body.querySelector('.mr-price__row');
    let breakdown = body.querySelector('[data-mr-service-breakdown]');

    if (service > 0 && row) {
      if (!breakdown) {
        breakdown = document.createElement('span');
        breakdown.className = 'mr-price__breakdown';
        breakdown.setAttribute('data-mr-service-breakdown', '');
        row.appendChild(breakdown);
      }
      const label = this.dataset.breakdownLabel || 'grabado';
      breakdown.textContent = `${this.#money(base)} + ${label} +${this.#money(service)}`;
    } else {
      breakdown?.remove();
    }
  };

  /** @param {number} cents */
  #money(cents) {
    const format = this.dataset.moneyFormat ?? '{{amount}}';
    const withComma = format.includes('amount_with_comma_separator');
    const number = (cents / 100).toLocaleString(withComma ? 'es-ES' : 'en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    return format.replace(/\{\{\s*amount[^}]*\}\}/, number);
  }
}

/**
 * Cambia el importe de un elemento de precio sin tocar el texto oculto para
 * lectores de pantalla («Precio de oferta»), que va en un span aparte.
 *
 * @param {Element | null} element
 * @param {string} text
 */
function setAmount(element, text) {
  if (!element) return;

  const nodes = [...element.childNodes].filter(
    (node) => node.nodeType === Node.TEXT_NODE && node.textContent?.trim()
  );
  const last = nodes.at(-1);

  if (last) {
    last.textContent = ` ${text} `;
  } else if (!element.children.length) {
    element.textContent = text;
  }
}

if (!customElements.get('mr-product-price')) {
  customElements.define('mr-product-price', MrProductPrice);
}
